import React from 'react'
import { useTranslation } from 'react-i18next'
import { KibCloseIcon } from '@chewy/kib-icons-react'
import KibTag from '@chewy/kib-tag-react'
import classNames from 'classnames'

import RhapTextButton from '../common/RhapTextButton'
import ChatSessionBar from './ChatSessionBar'
import PopOutIcon from './icons/PopOutIcon'
import RightDockIcon from './icons/RightDockIcon'

import '@chewy/kib-tag-styles/dist/kib-tag.css'
import styles from './ChatWindowHeader.module.scss'

interface ChatWindowHeaderProps {
  draggable?: boolean
  isFloating?: boolean
  onClose: () => void
  onMouseDown?: (e: React.MouseEvent) => void
  onToggleMode?: () => void
  title: string
}

const ChatWindowHeader: React.FC<ChatWindowHeaderProps> = ({
  title,
  onClose,
  onToggleMode,
  draggable = false,
  isFloating = false,
  onMouseDown,
}) => {
  const { t } = useTranslation('Agent')

  return (
    <div className={styles.windowHeaderContainer}>
      <div
        aria-label={draggable ? t('DRAGGABLE_WINDOW_HEADER') : undefined}
        className={classNames(styles.windowHeader, {
          [styles.draggable]: draggable,
        })}
        role="button"
        tabIndex={draggable ? 0 : -1}
        onKeyDown={(e) => {
          if (draggable && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault()
            // For draggable headers, we don't want to trigger any action on Enter/Space
            // The dragging is handled by mouse events only
          }
        }}
        onMouseDown={draggable ? onMouseDown : undefined}
      >
        <div className={styles.titleSection}>
          <span className={styles.windowTitle}>{title}</span>
          <div className={styles.betaPillWrapper}>
            <KibTag theme="default">{t('BETA')}</KibTag>
          </div>
        </div>
        <div className={styles.windowControls}>
          {onToggleMode && (
            <RhapTextButton
              iconOnly
              aria-label={
                isFloating
                  ? t('SWITCH_TO_RAIL_VIEW')
                  : t('SWITCH_TO_FLOATING_VIEW')
              }
              emphasis="secondary"
              icon={isFloating ? <RightDockIcon /> : <PopOutIcon />}
              size="small"
              theme="utility"
              title={
                isFloating
                  ? t('SWITCH_TO_RAIL_VIEW')
                  : t('SWITCH_TO_FLOATING_VIEW')
              }
              onClick={onToggleMode}
            />
          )}
          <RhapTextButton
            iconOnly
            aria-label={t('CLOSE_WINDOW')}
            emphasis="secondary"
            icon={<KibCloseIcon />}
            size="small"
            theme="utility"
            title={t('CLOSE_WINDOW')}
            onClick={onClose}
          />
        </div>
      </div>
      <ChatSessionBar />
    </div>
  )
}

export default ChatWindowHeader
